<template>
  <div class="otp-verification-container">
    <div class="otp-verification-card">
      <!-- Logo Section -->
      <div class="auth-logo">
        <h1>
          <i class="fas fa-route me-2"></i>
          Gypsy Souls
        </h1>
        <p>{{ isSignup ? "Complete Registration" : "Welcome Back!" }}</p>
      </div>

      <!-- OTP Verification -->
      <div class="auth-step">
        <h4 class="step-title">
          <i class="fas fa-shield-alt me-2"></i>
          Verify OTP
        </h4>
        <p class="step-subtitle">
          Enter the 4-digit code sent to +{{ countryCode }}{{ mobileNumber }}
        </p>

        <form @submit.prevent="verifyOTP">
          <div class="mb-3">
            <label for="otp" class="form-label">Verification Code</label>
            <input
              type="text"
              class="form-control text-center"
              id="otp"
              v-model="form.otp"
              :class="{ 'is-invalid': errors.otp }"
              placeholder="Enter 4-digit OTP"
              maxlength="4"
              pattern="[0-9]{4}"
              style="font-size: 1.5rem; letter-spacing: 0.5rem"
              required
              autocomplete="one-time-code"
            />
            <div v-if="errors.otp" class="invalid-feedback">
              {{ errors.otp }}
            </div>
          </div>

          <!-- Timer and Resend -->
          <div class="text-center mb-3">
            <div v-if="resendTimer > 0" class="text-muted">
              <i class="fas fa-clock me-1"></i>
              Resend OTP in {{ resendTimer }}s
            </div>
            <button
              v-else
              type="button"
              class="btn btn-link p-0"
              @click="resendOTP"
              :disabled="isLoading"
            >
              <i class="fas fa-redo me-1"></i>
              Resend OTP
            </button>
          </div>

          <!-- Error Alert -->
          <div v-if="error" class="alert alert-danger" role="alert">
            <i class="fas fa-exclamation-triangle me-2"></i>
            {{ error }}
          </div>

          <!-- Action Buttons -->
          <div class="d-flex gap-2">
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="goBack"
            >
              <i class="fas fa-arrow-left me-2"></i>
              Back
            </button>
            <button
              type="submit"
              class="btn btn-primary flex-fill"
              :disabled="isLoading"
            >
              <span
                v-if="isLoading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
              >
                <span class="visually-hidden">Loading...</span>
              </span>
              <i v-else class="fas fa-check me-2"></i>
              {{
                isLoading
                  ? "Verifying..."
                  : isSignup
                  ? "Complete Signup"
                  : "Login"
              }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import Swal from "sweetalert2";
import axios from "axios";

export default {
  name: "MobileOTPVerification",
  data() {
    return {
      form: {
        otp: "",
      },
      errors: {},
      error: null,
      isLoading: false,
      mobileNumber: "",
      countryCode: "91",
      verificationId: "",
      username: "",
      isSignup: false,
      resendTimer: 0,
      resendInterval: null,
    };
  },

  created() {
    // Get data from query params
    this.mobileNumber = this.$route.query.mobile || "";
    this.countryCode = this.$route.query.countryCode || "91";
    this.verificationId = this.$route.query.verificationId || "";
    this.username = this.$route.query.username || "";
    this.isSignup = this.$route.query.isSignup === "true";

    if (!this.mobileNumber || !this.verificationId) {
      // Redirect to login if missing required data
      this.$router.push("/login");
    }

    this.startResendTimer();
  },

  methods: {
    ...mapActions("auth", ["mobileLogin"]),

    validateOTP() {
      this.errors = {};

      if (!this.form.otp) {
        this.errors.otp = "OTP is required";
      } else if (!/^\d{4}$/.test(this.form.otp)) {
        this.errors.otp = "Please enter a valid 4-digit OTP";
      }

      return Object.keys(this.errors).length === 0;
    },

    async verifyOTP() {
      if (!this.validateOTP()) {
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const response = await axios.post("/mobile/auth/verify-otp", {
          username: this.username,
          mobileNumber: this.mobileNumber,
          countryCode: this.countryCode,
          verificationId: this.verificationId,
          otp: this.form.otp,
        });

        if (response.data.success) {
          // Store user data in Vuex
          await this.mobileLogin(response.data);

          // Show success message
          await Swal.fire({
            title: response.data.user.isNewUser
              ? "Welcome to Gypsy Souls!"
              : "Welcome Back!",
            text: response.data.user.isNewUser
              ? "Your account has been created successfully"
              : "Login successful",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });

          // Redirect to home page
          this.$router.push({ name: "PublicTrips" });
        } else {
          throw new Error(response.data.message || "Verification failed");
        }
      } catch (error) {
        console.error("Verify OTP error:", error);
        this.error =
          error.response?.data?.error || error.message || "Verification failed";

        Swal.fire({
          title: "Verification Failed",
          text: this.error,
          icon: "error",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 5000,
        });
      } finally {
        this.isLoading = false;
      }
    },

    async resendOTP() {
      if (this.resendTimer > 0) return;

      this.isLoading = true;
      this.error = null;

      try {
        const response = await axios.post("/mobile/auth/send-otp", {
          username: this.username,
          mobileNumber: this.mobileNumber,
          countryCode: this.countryCode,
        });

        if (response.data.success) {
          this.verificationId = response.data.verificationId;
          this.startResendTimer();

          Swal.fire({
            title: "OTP Resent!",
            text: "New verification code sent to your mobile",
            icon: "success",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          });
        } else {
          throw new Error(response.data.message || "Failed to resend OTP");
        }
      } catch (error) {
        console.error("Resend OTP error:", error);
        this.error =
          error.response?.data?.error ||
          error.message ||
          "Failed to resend OTP";
      } finally {
        this.isLoading = false;
      }
    },

    goBack() {
      if (this.isSignup) {
        this.$router.push({
          name: "MobileSignup",
          query: {
            mobile: this.mobileNumber,
            countryCode: this.countryCode,
          },
        });
      } else {
        this.$router.push("/login");
      }
    },

    startResendTimer() {
      this.resendTimer = 60;
      this.resendInterval = setInterval(() => {
        this.resendTimer--;
        if (this.resendTimer <= 0) {
          this.clearResendTimer();
        }
      }, 1000);
    },

    clearResendTimer() {
      if (this.resendInterval) {
        clearInterval(this.resendInterval);
        this.resendInterval = null;
      }
      this.resendTimer = 0;
    },
  },

  beforeUnmount() {
    this.clearResendTimer();
  },
};
</script>

<style scoped>
.otp-verification-container {
  background: linear-gradient(135deg, #2563eb 0%, #059669 100%);
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.otp-verification-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  position: relative;
  overflow: hidden;
}

.otp-verification-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #2563eb, #059669);
}

.auth-logo {
  text-align: center;
  margin-bottom: 2rem;
}

.auth-logo h1 {
  background: linear-gradient(135deg, #2563eb, #059669);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.auth-logo p {
  color: #6c757d;
  font-size: 0.9rem;
  margin: 0;
}

.auth-step {
  animation: slideIn 0.3s ease-out;
}

.step-title {
  color: #333;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
}

.step-subtitle {
  color: #6c757d;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
}

.form-control:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.25);
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #059669);
  border: none;
  font-weight: 600;
  padding: 0.75rem;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #1d4ed8, #047857);
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(37, 99, 235, 0.4);
}

.btn-outline-secondary {
  border-color: #6c757d;
  color: #6c757d;
  border-radius: 10px;
  font-weight: 600;
}

.btn-outline-secondary:hover {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-link {
  color: #2563eb;
  text-decoration: none;
  font-weight: 600;
}

.btn-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.alert {
  border-radius: 10px;
  border: none;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (max-width: 576px) {
  .otp-verification-card {
    margin: 1rem;
    padding: 1.5rem;
  }

  .auth-logo h1 {
    font-size: 1.75rem;
  }

  .step-title {
    font-size: 1.1rem;
  }
}
</style>
